import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/layouts/Footer";
import SELLING_TERMS from "../../utils/selling-terms.json";
import { useStore } from "../../store";
import PrelaunchFooter from "../../components/layouts/PrelaunchFooter";

type SubItem = { heading?: string; text?: string };
type Section = {
  id: number;
  heading: string;
  text?: string;
  sub?: SubItem[];
  ending?: string;
};

const SECTIONS = SELLING_TERMS as Section[];
const LAST_UPDATED = "April 2026";

// Read deep-link hash synchronously so the first render lands on the
// requested section — avoids a "#0 → #5" flicker on mount.
const readHashSection = () => {
  if (typeof window === "undefined") return 0;
  const fromHash = parseInt(window.location.hash.replace("#", ""), 10);
  if (!Number.isNaN(fromHash) && fromHash >= 0 && fromHash < SECTIONS.length) {
    return fromHash;
  }
  return 0;
};

function SellingTerms() {
  const { isFromPrelaunch } = useStore((s: any) => s);
  const [activeTab, setActiveTab] = useState<number>(readHashSection);
  const [navOpen, setNavOpen] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const selected = useMemo<Section | null>(
    () => SECTIONS[activeTab] ?? null,
    [activeTab]
  );

  const handleSelect = useCallback(
    (idx: number, opts?: { scroll?: boolean }) => {
      if (idx < 0 || idx >= SECTIONS.length) return;
      setActiveTab(idx);
      setNavOpen(false);
      if (opts?.scroll && contentRef.current) {
        contentRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    []
  );

  useEffect(() => {
    const next = `#${activeTab}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, "", next);
    }
  }, [activeTab]);

  const goPrev = () => handleSelect(activeTab - 1, { scroll: true });
  const goNext = () => handleSelect(activeTab + 1, { scroll: true });

  return (
    <>
      <section className="termsHero">
        <div className="container">
          <nav className="termsHero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span>Selling Terms &amp; Conditions</span>
          </nav>
          <div className="termsHero__title">
            <span className="termsHero__badge">
              <i className="bi bi-shield-check" aria-hidden="true" />
              Legal
            </span>
            <h1>Selling Terms &amp; Conditions</h1>
            <p className="termsHero__lead">
              The terms that govern listing and selling artworks on the
              Boll-e&trade; marketplace &mdash; so every sale stays fair and
              transparent.
            </p>
            <div className="termsHero__meta">
              <span>
                <i className="bi bi-calendar3" aria-hidden="true" />
                Last updated {LAST_UPDATED}
              </span>
              <span aria-hidden="true">·</span>
              <span>
                <i className="bi bi-list-ol" aria-hidden="true" />
                {SECTIONS.length} sections
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="container termsPage" ref={contentRef}>
        <button
          type="button"
          className="termsNav__toggle"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <i className="bi bi-list" aria-hidden="true" />
          <span>
            Section {activeTab + 1} &middot; {selected?.heading}
          </span>
          <i
            className={`bi ${navOpen ? "bi-chevron-up" : "bi-chevron-down"}`}
            aria-hidden="true"
          />
        </button>

        <div className="row g-4">
          <aside
            className={`col-12 col-lg-4 col-xl-3 termsNav${
              navOpen ? " termsNav--open" : ""
            }`}
          >
            <div className="termsNav__sticky">
              <p className="termsNav__title">In this document</p>
              <ol className="termsNav__list">
                {SECTIONS.map((s, i) => (
                  <li
                    key={s.id}
                    className={`termsNav__item${
                      i === activeTab ? " termsNav__item--active" : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(i, { scroll: true })}
                    >
                      <span className="termsNav__num">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="termsNav__label">{s.heading}</span>
                    </button>
                  </li>
                ))}
              </ol>
              <div className="termsNav__progress">
                <div
                  className="termsNav__progressBar"
                  style={{
                    width: `${((activeTab + 1) / SECTIONS.length) * 100}%`,
                  }}
                />
                <span>
                  {activeTab + 1} of {SECTIONS.length}
                </span>
              </div>
            </div>
          </aside>

          <main className="col-12 col-lg-8 col-xl-9">
            <article className="termsCard" key={activeTab}>
              <header className="termsCard__head">
                <span className="termsCard__kicker">
                  Section {String(activeTab + 1).padStart(2, "0")}
                </span>
                <h2>{selected?.heading}</h2>
              </header>

              {selected?.text && (
                <p className="termsCard__lead">{selected.text}</p>
              )}

              {selected?.sub && selected.sub.length > 0 && (
                <ol className="termsCard__list">
                  {selected.sub.map((item, idx) => (
                    <li key={idx} className="termsCard__listItem">
                      <span className="termsCard__listNum">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div className="termsCard__listBody">
                        {item.heading && (
                          <h3 className="termsCard__listHeading">
                            {item.heading}
                          </h3>
                        )}
                        {item.text && (
                          <p className="termsCard__listText">{item.text}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}

              {selected?.ending && (
                <aside className="termsCard__ending">
                  <i className="bi bi-info-circle" aria-hidden="true" />
                  <p>{selected.ending}</p>
                </aside>
              )}

              <footer className="termsCard__nav">
                <button
                  type="button"
                  className="termsCard__navBtn"
                  onClick={goPrev}
                  disabled={activeTab === 0}
                >
                  <i className="bi bi-arrow-left" aria-hidden="true" />
                  <span className="termsCard__navLabel">
                    <span className="termsCard__navHint">Previous</span>
                    <span className="termsCard__navTitle">
                      {activeTab > 0 ? SECTIONS[activeTab - 1].heading : "—"}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="termsCard__navBtn termsCard__navBtn--next"
                  onClick={goNext}
                  disabled={activeTab === SECTIONS.length - 1}
                >
                  <span className="termsCard__navLabel">
                    <span className="termsCard__navHint">Next</span>
                    <span className="termsCard__navTitle">
                      {activeTab < SECTIONS.length - 1
                        ? SECTIONS[activeTab + 1].heading
                        : "—"}
                    </span>
                  </span>
                  <i className="bi bi-arrow-right" aria-hidden="true" />
                </button>
              </footer>
            </article>

            <div className="termsContact">
              <div>
                <h4>Questions about these terms?</h4>
                <p>We're happy to walk you through anything that's unclear.</p>
              </div>
              <Link
                to="/help-and-support?to=contact-us"
                className="termsContact__btn"
              >
                Contact support
                <i className="bi bi-arrow-up-right" aria-hidden="true" />
              </Link>
            </div>
          </main>
        </div>
      </div>

      {isFromPrelaunch ? <PrelaunchFooter /> : <Footer />}
    </>
  );
}

export default SellingTerms;
